import React, { useContext, useCallback, useState, useMemo, useEffect } from 'react'
import classNames from 'classnames'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { DndContext } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

import Pencil from 'Assets/images/pencil.svg'
import Circle from 'Assets/images/circle.svg'

import QuickReplyEdit from './edit/QuickReplyEdit'

import { StepContext } from '../../contexts'
import { getFlowHistory, getActiveFlow } from 'Selectors/flow'
import { setFlowHistory, updateChoicesOrder } from 'Actions/flow'

import { findgoToStep } from '../utils/findGoToStep'
import StepLine from './StepLine'
import Draggable from './Draggable'

const QuickReply = (props) => {
  const { quickReply, actionId, choiceId, index, length } = props
  const dispatch = useDispatch()

  const [editMode, setEditMode] = useState(false)
  const history = useSelector(getFlowHistory)
  const activeFlow = useSelector(getActiveFlow)
  const [hover, setHover] = useState(false)

  const { row } = useContext(StepContext)

  const onQuickReplyClick = useCallback(
    (action) => {
      const steps = Object.keys(activeFlow?.conversation?.steps || {})
      const goToStep = findgoToStep(action)

      if (goToStep && !action.flowId && steps.length && steps.includes(goToStep)) {
        dispatch(setFlowHistory({ goToStep, row }))
      } else {
        setEditMode(true)
      }
    },
    [activeFlow, dispatch, row]
  )

  const onEditClick = () => setEditMode(true)
  const onCancelClick = () => {
    setEditMode(false)
    setHover(false)
  }

  const action = _.get(quickReply, 'action', {})

  const goToStep = useMemo(() => findgoToStep(action), [action])
  const hasActiveStep = goToStep && _.split(history, '.')[row] === goToStep

  const mouseOverEvents = {
    onMouseOver: () => setHover(true),
    onMouseLeave: () => setHover(false)
  }

  const isActive = hasActiveStep || hover

  useEffect(() => {
    if (!hasActiveStep) {
      setEditMode(false)
    }
  }, [hasActiveStep])

  return (
    <StepLine className={classNames({ 'pt-0': index === 0, 'pb-0': length === index })} {...mouseOverEvents}>
      <Draggable drag={!editMode} id={choiceId} index={index}>
        {(provided) => {
          return (
            <div className={classNames('quote-wrapper', { active: isActive, 'edit-mode': editMode })}>
              <div className='quote-item'>
                {editMode && (
                  <QuickReplyEdit
                    mode='edit'
                    actionId={actionId}
                    choiceId={choiceId}
                    quickReply={quickReply}
                    cancelFn={onCancelClick}
                  />
                )}
                {!editMode && (
                  <div className='pill-wrapper' onClick={() => onQuickReplyClick(quickReply.action)}>
                    <div className='pill'>
                      <div className='icon' {...provided.dragHandleProps}>
                        <Circle />
                      </div>
                      <div className='text'> {quickReply.text}</div>
                    </div>
                    <div className={classNames('actions', { 'd-none': !isActive })}>
                      <div className='action-icon edit' onClick={onEditClick} title='edit'>
                        <Pencil height='16' width='16' />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        }}
      </Draggable>
    </StepLine>
  )
}

const QuickReplies = (props) => {
  const { actionId, index } = props
  const { step, stepId } = useContext(StepContext)
  const dispatch = useDispatch()

  const settings = _.get(step, 'settings', {})
  const action = _.get(settings, `${actionId}`)
  const choices = _.get(action, 'choices', [])

  const handleDragEnd = (event) => {
    const { over, active } = event

    if (!over) {
      return
    }

    const sourceIndex = choices.findIndex((id) => id === active.id)
    const destinationIndex = choices.findIndex((id) => id === over.id)

    dispatch(updateChoicesOrder({ stepId, actionId, sourceIndex, destinationIndex }))
  }

  return (
    <StepLine>
      <Draggable drag id={actionId} index={index}>
        <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
          <SortableContext items={choices}>
            {_.map(choices, (choiceId, choiceIndex) => {
              const qr = _.get(action, `settings.${choiceId}`, {})

              return (
                <QuickReply
                  key={choiceId}
                  actionId={actionId}
                  choiceId={choiceId}
                  quickReply={qr}
                  index={choiceIndex}
                  length={choices.length - 1}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </Draggable>
    </StepLine>
  )
}

export default QuickReplies
